import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { Reveal } from "../lib/motion";
import { IMG } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", age: "", city: "", contact: "",
  mode: "image", platforms_experience: "", about: "", consent_18: false,
};

export const ApplyForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.age || !form.city || !form.contact) {
      toast.error("Uzupełnij wymagane pola.");
      return;
    }
    if (!form.consent_18) {
      toast.error("Potwierdź, że masz ukończone 18 lat.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, { ...form, age: parseInt(form.age, 10) });
      setDone(true);
      toast.success("Zgłoszenie wysłane! Odezwiemy się dyskretnie.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Coś poszło nie tak. Spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 px-4 rounded-lg bg-graphite/70 border border-white/12 text-white placeholder:text-white/35 focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30 transition-all";

  return (
    <section id="aplikuj" className="relative overflow-hidden" data-testid="apply-section">
      {/* background */}
      <img src={IMG.cta} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "right center" }} loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-graphite via-graphite/70 to-graphite/25" />
      <div className="absolute inset-0 bg-graphite/25" />

      <div className="relative z-10 container-pm py-[clamp(64px,9vw,120px)] grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="eyebrow mb-4">Cel: rekrutacja</p>
          <h2 className="font-display font-semibold text-white text-[clamp(34px,4.6vw,60px)] leading-[1.02] tracking-[-0.04em]">
            Gotowa na zmianę? <br />
            <span className="text-coral">Porozmawiajmy.</span>
          </h2>
          <p className="mt-6 text-white/75 max-w-[42ch] text-base sm:text-lg">
            Szukamy atrakcyjnych, ambitnych dziewczyn gotowych na wielkie pieniądze.
            Wypełnij formularz — odpowiadamy szybko, dyskretnie i bez zobowiązań.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/70">
            {["Odpowiedź zwykle w 24h", "Pełna poufność (NDA)", "Rozliczenie procentowe — zarabiasz razem z nami"].map((x) => (
              <li key={x} className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-coral" /> {x}
              </li>
            ))}
          </ul>
          <a
            href="https://wa.me/48571309237"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="apply-whatsapp"
            className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-[#25D366] text-white font-semibold hover:bg-[#1EBE5D] transition-colors"
          >
            <MessageCircle size={18} /> Wolisz WhatsApp? Napisz od razu
          </a>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="pm-card p-7 sm:p-9">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                  data-testid="apply-success"
                >
                  <CheckCircle2 className="mx-auto text-coral mb-5" size={52} strokeWidth={1.4} />
                  <h3 className="font-display text-2xl text-white mb-2">Dziękujemy!</h3>
                  <p className="text-white/65 max-w-[32ch] mx-auto">
                    Twoje zgłoszenie do PMPOINT trafiło do nas. Odezwiemy się dyskretnie w ciągu 24h.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-6 text-sm text-coral hover:underline"
                    data-testid="apply-reset"
                  >
                    Wyślij kolejne zgłoszenie
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                  data-testid="apply-form"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="apply-name" className={inputCls} placeholder="Imię *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                    <input data-testid="apply-age" type="number" min="18" className={inputCls} placeholder="Wiek *" value={form.age} onChange={(e) => set("age", e.target.value)} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="apply-city" className={inputCls} placeholder="Miasto / kraj *" value={form.city} onChange={(e) => set("city", e.target.value)} />
                    <input data-testid="apply-contact" className={inputCls} placeholder="Kontakt (Telegram / Instagram) *" value={form.contact} onChange={(e) => set("contact", e.target.value)} />
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-[0.12em] text-white/45 mb-2">Model współpracy</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[{ v: "image", l: "Z wizerunkiem" }, { v: "independent", l: "Anonimowo" }].map((o) => (
                        <button
                          type="button"
                          key={o.v}
                          data-testid={`apply-mode-${o.v}`}
                          onClick={() => set("mode", o.v)}
                          className={`h-11 rounded-lg text-sm font-semibold border transition-all ${
                            form.mode === o.v
                              ? "bg-coral text-white border-coral"
                              : "bg-graphite/70 text-white/70 border-white/12 hover:border-white/30"
                          }`}
                        >
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>

                  <input data-testid="apply-experience" className={inputCls} placeholder="Doświadczenie na platformach (opcjonalnie)" value={form.platforms_experience} onChange={(e) => set("platforms_experience", e.target.value)} />
                  <textarea data-testid="apply-about" rows={3} className={`${inputCls} h-auto py-3 resize-none`} placeholder="Kilka słów o sobie (opcjonalnie)" value={form.about} onChange={(e) => set("about", e.target.value)} />

                  <label className="flex items-start gap-3 cursor-pointer text-sm text-white/70">
                    <input
                      type="checkbox"
                      data-testid="apply-consent"
                      checked={form.consent_18}
                      onChange={(e) => set("consent_18", e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#ff4d67]"
                    />
                    <span>Potwierdzam, że mam ukończone 18 lat i zgadzam się na kontakt w sprawie współpracy.</span>
                  </label>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="apply-submit"
                    className="w-full h-[52px] rounded-lg bg-coral text-white font-bold flex items-center justify-center gap-2 hover:bg-coral-hover transition-colors disabled:opacity-60"
                  >
                    {loading ? <><Loader2 size={18} className="animate-spin" /> Wysyłanie...</> : <>Wyślij zgłoszenie <Send size={17} /></>}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
